/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col, Box, Button, Loader } from "@nordcloud/gnui";
import {
  useDriftRecordsQuery,
  useUpdateDriftsMutation,
  DriftRecordsQuery,
  DriftedResourcesCountQuery,
  DriftedResourcesCountQueryVariables,
  DriftedResourcesCountDocument,
} from "~/generated/graphql";
import {
  Pagination,
  ErrorMessage,
  BreadcrumbsBox,
  ReactTable,
} from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useQueryState } from "~/hooks";
import { showSuccess, showError } from "~/services/toast";
import { DriftListColumns } from "./DriftListColumns";

type ResourceRow = NonNullable<
  NonNullable<DriftRecordsQuery["driftedResources"]>["resources"]
>[0] & {
  action: (ids: string[], accept: boolean) => Promise<void>;
};

export function DriftReportPage() {
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [rows, setRows] = React.useState<ResourceRow[]>([]);

  const {
    state: { page, limit },
  } = useQueryState();
  const { data, error, loading, refetch } = useDriftRecordsQuery({
    variables: { limit, page },
  });

  const [updateDrift, { loading: loadingUpdate, error: errorUpdate, client }] =
    useUpdateDriftsMutation();

  const onDriftUpdate = React.useCallback(
    async (ids: string[], accept: boolean) => {
      try {
        const response = await updateDrift({
          variables: { ids, accept },
        });

        if (response.data?.updateDriftStatuses) {
          client.cache.updateQuery<
            DriftedResourcesCountQuery,
            DriftedResourcesCountQueryVariables
          >(
            {
              query: DriftedResourcesCountDocument,
            },
            (driftedResourcesData) => {
              const count = driftedResourcesData?.driftedResources?.count ?? 0;
              const updatedDriftsCount = ids.length;

              return {
                driftedResources: {
                  count: count > 0 ? count - updatedDriftsCount : count,
                },
              };
            }
          );
        }

        refetch();

        if (accept) {
          showSuccess(SUCCESS_TEXT.estateRecordsApproved);
        } else {
          showSuccess(SUCCESS_TEXT.estateRecordsRejected);
        }
      } catch {
        showError(ERROR_TEXT.default);
      }
    },
    [refetch, updateDrift]
  );

  React.useEffect(() => {
    const resources = data?.driftedResources?.resources ?? [];

    const resourceRows = resources.map((row) => {
      return { ...row, action: onDriftUpdate };
    });

    setRows(resourceRows);
  }, [data, onDriftUpdate]);

  const handleApprove = (action: boolean) => {
    onDriftUpdate(selectedIds, action);
  };

  const columns = React.useMemo(() => DriftListColumns, []);

  return (
    <Row>
      <Col>
        <ErrorMessage error={error || errorUpdate} />
        <BreadcrumbsBox title="Estate records">
          <div css={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={selectedIds.length <= 0}
              severity="medium"
              onClick={() => handleApprove(true)}
            >
              Approve selected
            </Button>
            <div css={{ marginLeft: "1rem" }}>
              <Button
                disabled={selectedIds.length <= 0}
                severity="medium"
                onClick={() => handleApprove(false)}
              >
                Reject selected
              </Button>
            </div>
          </div>
        </BreadcrumbsBox>
        <Box margin="spacing04">
          {(loading || loadingUpdate) && <Loader />}
          <ErrorMessage error={error} />
          {data && !loading && (
            <ReactTable
              columns={columns}
              data={rows}
              onSelectedRowsChange={setSelectedIds}
            />
          )}
          <Pagination count={data?.driftedResources?.count || 0} />
        </Box>
      </Col>
    </Row>
  );
}
